import React from "react";
import Grid from "@material-ui/core/Grid";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import { NO_ELEMENTS_MATCHED, SUCCESS } from "../constants/resultCodes";
import * as types from "../constants";

// 일관된 레이아웃과 가로 스크롤바 방지, 버튼 높이 동일화, 단일 행 버튼 표시, 360px에서 버튼 표시
const useStyles = makeStyles((theme) => ({
  dialogContainer: {
    overflowX: "hidden", // 가로 스크롤바 방지
    maxWidth: "360px", // 360px 뷰포트에 맞게
    [theme.breakpoints.up("sm")]: {
      maxWidth: "420px", // 비모바일에서 더 넓게
    },
    margin: "0 auto", // 다이얼로그 중앙 정렬
    padding: "12px", // 일관된 패딩
  },
  dialogTitle: {
    fontFamily: "Roboto, sans-serif",
    fontWeight: 800,
    fontSize: "18px", // 고정된 폰트 크기
    color: "#1A3C5E",
    textAlign: "center",
    padding: "20px 0 20px 0", // 타이틀 상하단 고정 패딩
  },
  dialogContentTop: {
    fontFamily: "Roboto, sans-serif",
    fontSize: "14px",
    color: "#1A3C5E",
    textAlign: "center",
    paddingBottom: "8px",
  },
  dialogContent: {
    fontFamily: "Roboto, sans-serif",
    fontSize: "14px",
    color: "#1A3C5E",
    textAlign: "center",
  },
  bulletText: {
    marginTop: "8px",
    marginBottom: "8px",
  },
  highlightedText: {
    color: "#3DB5E6",
    fontWeight: "bold",
  },
  closeButton: {
    fontFamily: "Roboto, sans-serif",
    fontSize: "14px",
    color: "#F28C38",
    borderColor: "#F28C38",
    textTransform: "none",
    width: "130px", // 360px 뷰포트에 맞게
    height: "40px", // 고정 높이
    borderRadius: "8px",
    padding: "8px 8px", // 패딩 조정
    lineHeight: "1.2", // 텍스트 정렬 조정
    [theme.breakpoints.up("sm")]: {
      width: "180px", // 비모바일에서 더 큰 너비
      padding: "8px 16px",
      whiteSpace: "nowrap", // 비모바일에서 텍스트 한 줄
    },
  },
  cancelButton: {
    fontFamily: "Roboto, sans-serif",
    fontSize: "14px",
    backgroundColor: "#F28C38",
    color: "#FFFFFF",
    textTransform: "none",
    width: "130px", // 360px 뷰포트에 맞게
    height: "40px", // 고정 높이
    borderRadius: "8px",
    padding: "8px 8px", // 패딩 조정
    lineHeight: "1.2", // 텍스트 정렬 조정
    "&:hover": {
      backgroundColor: "#E07B30",
    },
    [theme.breakpoints.up("sm")]: {
      width: "180px", // 비모바일에서 더 큰 너비
      padding: "8px 16px",
      whiteSpace: "nowrap", // 비모바일에서 텍스트 한 줄
    },
  },
  dialogActions: {
    padding: "30px 0 20px 0", // 버튼 하단 고정 패딩
    display: "flex",
    justifyContent: "center",
  },
  buttonContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "row", // 버튼을 단일 행으로 표시
    flexWrap: "nowrap", // 줄바꿈 방지
    justifyContent: "center",
    gap: "10px", // 버튼 간 간격
  },
}));

const AnalysisInProgressDialog = ({ handleClose, handleCancel, reportId, span, collected }) => {
  const classes = useStyles();

  const handleCancelAnalysis = () => {
    handleCancel();
    handleClose();
  };

  const totalSpan = parseInt(span, 10) || 0;
  const collectedCount = parseInt(collected, 10) || 0;
  const timeLeft = Math.max(0, totalSpan - collectedCount);

  return (
    <Box className={classes.dialogContainer}>
      <DialogTitle className={classes.dialogTitle}>
        Analysis In Progress
      </DialogTitle>
      <DialogContent>
        <Grid container direction="column">
          <Grid item>
            <Typography className={classes.dialogContentTop}>
              This analysis is currently in progress and cannot be viewed yet
              {reportId && ` (Analysis ID: ${reportId.replace('MRR-', '').slice(-6)})`}
            </Typography>
          </Grid>
          <Grid item>
            <Typography className={`${classes.dialogContent} ${classes.bulletText}`}>
              • <strong>Collected Data:</strong>{" "}
              <span className={classes.highlightedText}>{collectedCount}</span> / {totalSpan} data points
            </Typography>
            <Typography className={`${classes.dialogContent} ${classes.bulletText}`}>
              • <strong>Approx. Time Left:</strong> ~
              <span className={classes.highlightedText}>{timeLeft}</span> hours
            </Typography>
            <Typography className={classes.dialogContent}>
              (May vary with network conditions)
            </Typography>
          </Grid>
          <Grid item>
            <Typography className={classes.dialogContent} style={{ marginTop: "12px" }}>
              Would you like to stop and cancel this analysis?
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <div className={classes.buttonContainer}>
          <Button
            variant="outlined"
            className={classes.closeButton}
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            variant="contained"
            className={classes.cancelButton}
            onClick={handleCancelAnalysis}
          >
            Yes, Cancel Analysis
          </Button>
        </div>
      </DialogActions>
    </Box>
  );
};

export default AnalysisInProgressDialog;
